/**
 * Phase 2 SHADOW Validation Panel (2026-05-08)
 *
 * Read-only audit panel that surfaces the data the operator needs to
 * decide if/when the SetProfile shadow engine is ready to graduate
 * from Phase 1 (audit-only) to Phase 2 (live recommendation). It
 * fetches `/api/tt/admin/setprofile-shadow-validation` and renders
 * five aggregated views:
 *
 *   • by_shadow_market   — n / WR % per market (mw, hc_fav, hc_dog, over, under)
 *   • by_ev_bucket       — n / WR % per EV bucket using conservative EV
 *   • by_profile_type    — n / WR % per SetProfile archetype
 *   • by_scout_only      — n / WR % split (scout_only true / false)
 *   • by_legacy_vs_shadow — agreement matrix (does legacy & shadow pick
 *                           the same market? when they disagree, did
 *                           shadow's pick win or lose?)
 *
 * Plus a per-match table (top 50 rows) showing exactly the fields the
 * user requested: legacy_recommended_market, shadow_recommended_market,
 * scout_only, ev_tier, profile_type, selected_ou_line, actual_cluster,
 * legacy_outcome, shadow_outcome.
 *
 * Pure presentational. NEVER changes Telegram, Kelly, Policy Guard,
 * or the live engine — Phase 2 activation requires explicit user
 * greenlight outside this UI.
 */
import React, { useEffect, useState } from 'react';
import { adminGet } from '../data/api';
import { ShieldCheck, RefreshCcw } from 'lucide-react';

const fmtInt = (v) => (v == null ? '—' : Number(v).toLocaleString('it-IT'));
const fmtPct = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);

const wrColor = (wr) => {
  if (wr == null) return 'text-zinc-500';
  if (wr >= 60) return 'text-emerald-300';
  if (wr >= 50) return 'text-emerald-200';
  if (wr >= 40) return 'text-amber-200';
  return 'text-rose-300';
};

function MiniTable({ title, testid, columns, rows, emptyText = '—' }) {
  return (
    <div data-testid={testid} className="rounded-sm border border-zinc-800 bg-zinc-950/40 p-2 space-y-1">
      <div className="text-[10px] text-zinc-400 tracking-wider font-semibold">{title}</div>
      {(!rows || rows.length === 0) ? (
        <div className="text-[10px] text-zinc-600 py-1">{emptyText}</div>
      ) : (
        <table className="w-full text-[10px]" style={{ fontFamily: 'monospace' }}>
          <thead>
            <tr className="text-zinc-500">
              {columns.map((c) => (
                <th key={c.key} className={c.align === 'right' ? 'text-right pl-2' : 'text-left'}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className="border-t border-zinc-900">
                {columns.map((c) => (
                  <td key={c.key}
                      className={`py-0.5 ${c.align === 'right' ? 'text-right pl-2' : ''} ${c.colorFn ? c.colorFn(row[c.key], row) : 'text-zinc-300'}`}>
                    {c.render ? c.render(row[c.key], row) : (row[c.key] ?? '—')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function SetProfileShadowValidationPanel({ pin, defaultDays = 30 }) {
  const [days, setDays] = useState(defaultDays);
  const [sinceDate, setSinceDate] = useState('');
  const [shadowOnly, setShadowOnly] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = async (d = days, sd = sinceDate, so = shadowOnly) => {
    setLoading(true);
    setErr(null);
    try {
      const params = new URLSearchParams();
      params.set('days', String(d));
      params.set('shadow_only', so ? 'true' : 'false');
      if (sd) params.set('since_date', sd);
      const r = await adminGet(
        `/tt/admin/setprofile-shadow-validation?${params.toString()}`,
        pin,
      );
      setData(r);
    } catch (e) {
      setErr(e?.message || 'shadow validation load failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(days, sinceDate, shadowOnly);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const aggregations = data?.aggregations || {};

  // Convert aggregation dicts to row arrays
  const dictToRows = (d, keyName) => Object.entries(d || {}).map(([k, v]) => ({
    [keyName]: k, ...v,
  }));

  const byShadowRows = dictToRows(aggregations.by_shadow_market, 'market')
    .sort((a, b) => (b.n || 0) - (a.n || 0));
  const byEvRows = dictToRows(aggregations.by_ev_bucket, 'bucket');
  const byProfileRows = dictToRows(aggregations.by_profile_type, 'profile')
    .sort((a, b) => (b.n || 0) - (a.n || 0));
  const byScoutRows = dictToRows(aggregations.by_scout_only, 'mode');
  const lvsRows = aggregations.by_legacy_vs_shadow || [];

  return (
    <div
      data-testid="setprofile-shadow-validation-panel"
      className="rounded-sm border border-zinc-800 bg-zinc-950/40 p-3 text-[11px] space-y-3"
      style={{ fontFamily: 'monospace' }}
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ShieldCheck size={13} className="text-violet-400" />
          <span className="text-zinc-100 font-semibold tracking-wide">
            SETPROFILE SHADOW · PHASE 2 VALIDATION
          </span>
          <span
            data-testid="phase-status-badge"
            className="text-[9px] px-1.5 py-0.5 border rounded-sm bg-violet-900/50 text-violet-200 border-violet-700"
          >
            {data?.phase_status || 'PHASE_1_SHADOW'}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="flex items-center gap-1 text-[10px] text-zinc-400">
            <input
              type="checkbox"
              data-testid="shadow-only-checkbox"
              checked={shadowOnly}
              onChange={(e) => {
                const v = e.target.checked;
                setShadowOnly(v);
                load(days, sinceDate, v);
              }}
            />
            shadow_only
          </label>
          <input
            type="date"
            data-testid="shadow-since-date"
            value={sinceDate}
            onChange={(e) => {
              const v = e.target.value;
              setSinceDate(v);
              load(days, v, shadowOnly);
            }}
            className="bg-zinc-900 border border-zinc-700 rounded-sm px-2 py-0.5 text-zinc-100 text-[10px]"
          />
          <select
            data-testid="shadow-days-select"
            value={days}
            onChange={(e) => {
              const d = Number(e.target.value);
              setDays(d);
              load(d, sinceDate, shadowOnly);
            }}
            className="bg-zinc-900 border border-zinc-700 rounded-sm px-2 py-0.5 text-zinc-100 text-[10px]"
          >
            {[7, 14, 30, 60, 90].map((d) => (
              <option key={d} value={d}>{`${d}d`}</option>
            ))}
          </select>
          <button
            data-testid="shadow-reload-btn"
            onClick={() => load(days, sinceDate, shadowOnly)}
            disabled={loading}
            className="flex items-center gap-1 px-2 py-1 rounded-sm bg-zinc-800/60 border border-zinc-700 text-zinc-300 hover:text-zinc-100 disabled:opacity-50"
          >
            <RefreshCcw size={11} className={loading ? 'animate-spin' : ''} />
            {loading ? 'loading' : 'reload'}
          </button>
        </div>
      </div>

      {err && (
        <div className="text-rose-300 px-2 py-1 rounded-sm bg-rose-950/40 border border-rose-800">
          {err}
        </div>
      )}

      {data && (
        <>
          <div
            data-testid="shadow-validation-filters"
            className="text-[10px] text-zinc-500 flex flex-wrap items-center gap-2"
          >
            cutoff {String(data.filters?.cutoff || '').slice(0, 10)} · shadow_only={String(data.filters?.shadow_only)}
            {' · '}n_analyses={fmtInt(data.n_analyses)}
            {' · '}n_settled={fmtInt(data.n_settled)}
            {' · '}coverage_unknown_shadow={fmtInt(data.coverage?.shadow_outcome_unknown)}
            {' · '}missing_favorite_side={fmtInt(data.coverage?.missing_favorite_side)}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <MiniTable
              title="BY SHADOW MARKET"
              testid="agg-by-shadow-market"
              columns={[
                { key: 'market', label: 'market' },
                { key: 'n', label: 'n', align: 'right' },
                { key: 'wins', label: 'W', align: 'right',
                  colorFn: () => 'text-emerald-300' },
                { key: 'losses', label: 'L', align: 'right',
                  colorFn: () => 'text-rose-300' },
                { key: 'voids', label: 'V', align: 'right',
                  colorFn: () => 'text-zinc-500' },
                { key: 'wr_pct', label: 'WR%', align: 'right',
                  render: fmtPct,
                  colorFn: (v) => wrColor(v) },
              ]}
              rows={byShadowRows}
              emptyText="no settled shadow matches"
            />
            <MiniTable
              title="BY EV BUCKET (shadow EV cons)"
              testid="agg-by-ev-bucket"
              columns={[
                { key: 'bucket', label: 'bucket' },
                { key: 'n', label: 'n', align: 'right' },
                { key: 'wins', label: 'W', align: 'right',
                  colorFn: () => 'text-emerald-300' },
                { key: 'losses', label: 'L', align: 'right',
                  colorFn: () => 'text-rose-300' },
                { key: 'wr_pct', label: 'WR%', align: 'right',
                  render: fmtPct,
                  colorFn: (v) => wrColor(v) },
              ]}
              rows={byEvRows}
            />
            <MiniTable
              title="BY PROFILE TYPE"
              testid="agg-by-profile-type"
              columns={[
                { key: 'profile', label: 'profile' },
                { key: 'n', label: 'n', align: 'right' },
                { key: 'wins', label: 'W', align: 'right',
                  colorFn: () => 'text-emerald-300' },
                { key: 'losses', label: 'L', align: 'right',
                  colorFn: () => 'text-rose-300' },
                { key: 'wr_pct', label: 'WR%', align: 'right',
                  render: fmtPct,
                  colorFn: (v) => wrColor(v) },
              ]}
              rows={byProfileRows}
            />
            <MiniTable
              title="BY SCOUT_ONLY"
              testid="agg-by-scout-only"
              columns={[
                { key: 'mode', label: 'mode' },
                { key: 'n', label: 'n', align: 'right' },
                { key: 'wins', label: 'W', align: 'right',
                  colorFn: () => 'text-emerald-300' },
                { key: 'losses', label: 'L', align: 'right',
                  colorFn: () => 'text-rose-300' },
                { key: 'wr_pct', label: 'WR%', align: 'right',
                  render: fmtPct,
                  colorFn: (v) => wrColor(v) },
              ]}
              rows={byScoutRows}
            />
          </div>

          <MiniTable
            title="LEGACY × SHADOW MARKET MATRIX"
            testid="agg-legacy-vs-shadow"
            columns={[
              { key: 'legacy_market', label: 'legacy' },
              { key: 'shadow_market', label: 'shadow' },
              { key: 'agree', label: 'agree?',
                render: (v) => (v ? '✓' : '✗'),
                colorFn: (v) => (v ? 'text-emerald-300' : 'text-rose-300') },
              { key: 'n', label: 'n', align: 'right' },
              { key: 'shadow_wins', label: 'sh.W', align: 'right',
                colorFn: () => 'text-emerald-300' },
              { key: 'shadow_losses', label: 'sh.L', align: 'right',
                colorFn: () => 'text-rose-300' },
              { key: 'shadow_voids', label: 'sh.V', align: 'right' },
            ]}
            rows={lvsRows}
          />

          {/* Per-match audit rows */}
          <div data-testid="shadow-validation-items" className="space-y-1">
            <div className="text-[10px] text-zinc-400 tracking-wider font-semibold">
              PER-MATCH AUDIT (top {(data.items || []).slice(0, 50).length} of {(data.items || []).length})
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[10px]" style={{ fontFamily: 'monospace' }}>
                <thead>
                  <tr className="text-zinc-500">
                    <th className="text-left">match_id</th>
                    <th className="text-left">profile</th>
                    <th className="text-left">legacy</th>
                    <th className="text-left">shadow</th>
                    <th className="text-left">scout?</th>
                    <th className="text-left">ev_tier</th>
                    <th className="text-right">ev cons</th>
                    <th className="text-right">ou_line</th>
                    <th className="text-left">cluster</th>
                    <th className="text-left">legacy_out</th>
                    <th className="text-left">shadow_out</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.items || []).slice(0, 50).map((it, i) => (
                    <tr key={i} className="border-t border-zinc-900">
                      <td className="text-zinc-400 text-[9px] truncate max-w-[180px]"
                          title={it.match_id}>{it.match_id}</td>
                      <td className="text-zinc-300">{it.profile_type || '—'}</td>
                      <td className="text-zinc-300">{it.legacy_recommended_market || '—'}</td>
                      <td className="text-amber-200 font-semibold">{it.shadow_recommended_market || '—'}</td>
                      <td className={it.scout_only ? 'text-amber-300' : 'text-zinc-500'}>
                        {it.scout_only ? 'scout' : 'live'}
                      </td>
                      <td className="text-zinc-400">{it.ev_tier || '—'}</td>
                      <td className="text-right text-zinc-300">
                        {it.shadow_ev_cons_pct == null ? '—' : `${Number(it.shadow_ev_cons_pct).toFixed(1)}%`}
                      </td>
                      <td className="text-right text-zinc-300">
                        {it.selected_ou_line ?? '—'}
                      </td>
                      <td className="text-zinc-300">{it.actual_cluster || '—'}</td>
                      <td className={`${
                        it.legacy_outcome === 'win' ? 'text-emerald-300'
                          : it.legacy_outcome === 'loss' ? 'text-rose-300'
                          : 'text-zinc-500'
                      }`}>{it.legacy_outcome || '—'}</td>
                      <td className={`${
                        it.shadow_outcome === 'win' ? 'text-emerald-300 font-bold'
                          : it.shadow_outcome === 'loss' ? 'text-rose-300 font-bold'
                          : 'text-zinc-500'
                      }`}>{it.shadow_outcome || '—'}</td>
                    </tr>
                  ))}
                  {(data.items || []).length === 0 && (
                    <tr>
                      <td colSpan={11}
                          className="text-center text-zinc-600 py-3">
                        No settled shadow analyses yet — keep observing in Phase 1.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="text-[9px] text-zinc-600 italic">
            Shadow Phase 1: this report is purely audit. Shadow recommendations
            never reach Telegram, Kelly, or Policy Guard. Phase 2 activation
            requires explicit user greenlight after observing ≥30 settled
            matches with stable WR% per market.
          </div>
        </>
      )}
    </div>
  );
}
